import {
  Box,
  Text,
  TogglerCheckbox,
  Tooltip,
  useToggle,
  zIndex,
} from '@factorialco/deprecated-design-system'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import i18n from 'modules/core/lib/i18n'

import SignersList from 'modules/documents/components/BulkFiles/_steps/EsignatureSettings/SignersList'
import SignersPopover from 'modules/documents/components/BulkFiles/_steps/EsignatureSettings/SignersPopover'

export interface SettingsValues {
  newEsignatureRequest: boolean
  newAdditionalSignersAccessIds: string[]
  newSignaturePlacement?: boolean
  hidden?: boolean
}

interface Props {
  settings: SettingsValues
  onChange: (value: SettingsValues) => void
  showRecipient?: boolean
  showSignaturePlacement?: boolean
  defaultSigner?: string
  signableFiles?: boolean
}

const EsignatureSettings = ({
  settings: {
    newEsignatureRequest,
    newAdditionalSignersAccessIds,
    newSignaturePlacement = false,
    hidden = false,
  },
  onChange,
  showRecipient = true,
  showSignaturePlacement = false,
  defaultSigner,
  signableFiles = true,
}: Props) => {
  const canPlaceSignatures = newAdditionalSignersAccessIds.length === 0
  const showSignersList = newAdditionalSignersAccessIds.length > 0 || showRecipient
  const [openEsignatureRequestTooltip, toggleEsignatureRequestTooltip] = useToggle()

  const addAdditionalSigners = (additionalSigners: string[]) => {
    onChange({
      newEsignatureRequest,
      newAdditionalSignersAccessIds: additionalSigners,
      newSignaturePlacement,
    })
  }

  const removeRecipient = () => {
    onChange({
      newEsignatureRequest: false,
      newAdditionalSignersAccessIds: [],
      newSignaturePlacement,
    })
  }

  const removeSigner = (idToRemove: string) => {
    const newAdditionalSigners = newAdditionalSignersAccessIds.filter(
      (signerId) => signerId !== idToRemove
    )
    onChange({
      newEsignatureRequest,
      newAdditionalSignersAccessIds: newAdditionalSigners,
      newSignaturePlacement,
    })
  }

  const tooltipAlert = !signableFiles
    ? i18n.t('documents.modal.esignature.modal.only_pdf_alert')
    : i18n.t('documents.modal.esignature.modal.only_visible_alert')

  return (
    <>
      <Box
        padding="s16"
        borderRadius={{ all: 'abs016' }}
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
        style={{ zIndex: zIndex.omnibutton }}
        background="grey000White"
      >
        <Box flexDirection="column" gap="s16">
          <Box width="full" justifyContent="spaceBetween" flexDirection="row">
            <>
              <Text.Normal weight="semibold">
                {i18n.t('documents.modal.esignature.modal.title')}
              </Text.Normal>
            </>
            <>
              <Tooltip.Alt
                attachment="bottom center"
                open={(hidden || !signableFiles) && openEsignatureRequestTooltip}
                toggle={toggleEsignatureRequestTooltip}
                label={tooltipAlert}
              >
                <TogglerCheckbox
                  value={newEsignatureRequest}
                  aria-label="esignature request"
                  onChange={(value) => {
                    const additionalSigners = [
                      ...(defaultSigner ? [defaultSigner] : []),
                      ...newAdditionalSignersAccessIds,
                    ]

                    onChange({
                      newEsignatureRequest: value,
                      newAdditionalSignersAccessIds: value ? additionalSigners : [],
                    })
                  }}
                  disabled={hidden || !signableFiles}
                />
              </Tooltip.Alt>
            </>
          </Box>

          <Text.Normal>{i18n.t('documents.modal.esignature.modal.only_pdf')}</Text.Normal>

          <Text.Normal size="200">
            <HelpCenterLink name="esignature">{i18n.t('esignature.learn_more')}</HelpCenterLink>
          </Text.Normal>
        </Box>
      </Box>
      {newEsignatureRequest && (
        <>
          <Box
            background="grey100"
            padding="s24"
            paddingBottom="s16"
            gap="s16"
            borderRadius={{
              bottomLeft: 'abs016',
              bottomRight: 'abs016',
            }}
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
            style={{ marginTop: '-12px' }}
          >
            {showSignersList && (
              <>
                <Text.Normal size="200" weight="medium">
                  {i18n.t('split_documents.form.signers')}
                </Text.Normal>
                <SignersList
                  additionalSigners={newAdditionalSignersAccessIds}
                  removeRecipient={removeRecipient}
                  removeSigner={removeSigner}
                  showRecipient={showRecipient}
                />
              </>
            )}
            <SignersPopover
              additionalSigners={newAdditionalSignersAccessIds}
              addAdditionalSigners={addAdditionalSigners}
            />
            {showSignaturePlacement && (
              <Box justifyContent="spaceBetween" gap="s8">
                <Text.Normal weight="semibold" size="300">
                  {i18n.t('documents.modal.esignature.modal.advanced_settings')}
                </Text.Normal>
                <TogglerCheckbox
                  value={newSignaturePlacement}
                  label={i18n.t('documents.beta.request_esignature_placement')}
                  onChange={(value) =>
                    onChange({
                      newEsignatureRequest,
                      newAdditionalSignersAccessIds,
                      newSignaturePlacement: value,
                    })
                  }
                  disabled={canPlaceSignatures}
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  )
}

export default EsignatureSettings
